import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  useToast,
  Code,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react';
import { ChevronRightIcon, EditIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const ViewTemplate = () => {
  const { id } = useParams();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (id && tenantId) {
      fetchTemplate();
    }
  }, [id, tenantId]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`/templates/${tenantId}/${id}`);
      
      if (!response || response.error) {
        throw new Error(response?.error || 'Template not found');
      }

      setTemplate(response);
    } catch (error) {
      console.error('Error fetching template:', error);
      toast({
        title: 'Error loading template',
        description: error.message || 'Failed to load template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate('/templates');
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'page': return 'blue';
      case 'component': return 'green';
      case 'layout': return 'purple';
      case 'email': return 'orange';
      default: return 'gray';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'page': return '📄';
      case 'component': return '🧩';
      case 'layout': return '🎨';
      case 'email': return '📧';
      default: return '📄';
    }
  };

  if (loading) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Spinner size="lg" />
          <Text>Loading template...</Text>
        </VStack>
      </Center>
    );
  }

  if (!tenantId) {
    return (
      <Container maxW="container.lg" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  if (!template) {
    return (
      <Container maxW="container.lg" py={6}>
        <Alert status="error">
          <AlertIcon />
          Template not found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/templates">Templates</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>{template.name}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <Box>
            <HStack spacing={3} mb={2}>
              <Text fontSize="xl">{getTypeIcon(template.template_type)}</Text>
              <Heading size="lg">{template.name}</Heading>
              <Badge colorScheme={getTypeColor(template.template_type)}>
                {template.template_type}
              </Badge>
              <Badge colorScheme={template.is_active ? 'green' : 'gray'}>
                {template.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </HStack>
            <Text color="gray.500">
              {template.description || 'No description provided'}
            </Text>
          </Box>
          <HStack>
            <Button 
              variant="outline" 
              onClick={() => navigate('/templates')}
            >
              Back to Templates
            </Button>
            <Button
              leftIcon={<EditIcon />}
              colorScheme="blue"
              onClick={() => navigate(`/templates/${id}/edit`)}
            >
              Edit Template
            </Button>
          </HStack>
        </HStack>

        {/* Template Details */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardHeader>
            <Heading size="md">Template Information</Heading>
          </CardHeader>
          <CardBody>
            <Table variant="simple">
              <Tbody>
                <Tr>
                  <Th width="200px">Template ID</Th>
                  <Td>{template.id}</Td>
                </Tr>
                <Tr>
                  <Th>Name</Th>
                  <Td>{template.name}</Td>
                </Tr>
                <Tr>
                  <Th>Type</Th>
                  <Td>
                    <HStack>
                      <Text>{getTypeIcon(template.template_type)}</Text>
                      <Badge colorScheme={getTypeColor(template.template_type)}>
                        {template.template_type}
                      </Badge>
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Th>Status</Th>
                  <Td>
                    <Badge colorScheme={template.is_active ? 'green' : 'gray'}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                </Tr>
                <Tr>
                  <Th>Description</Th>
                  <Td>{template.description || 'No description provided'}</Td>
                </Tr>
                <Tr>
                  <Th>Created</Th>
                  <Td>{new Date(template.created_at).toLocaleString()}</Td>
                </Tr>
                <Tr>
                  <Th>Last Updated</Th>
                  <Td>{new Date(template.updated_at).toLocaleString()}</Td>
                </Tr>
              </Tbody>
            </Table>
          </CardBody>
        </Card>

        {/* Template Configuration */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardHeader>
            <Heading size="md">Template Configuration</Heading>
          </CardHeader>
          <CardBody>
            <Tabs>
              <TabList>
                <Tab>Layout Structure</Tab>
                <Tab>Styles</Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <Text fontSize="sm" color="gray.600">
                      Layout structure configuration
                    </Text>
                    
                    {template.layout_structure ? (
                      <Code
                        display="block"
                        whiteSpace="pre"
                        p={4}
                        borderRadius="md"
                        fontSize="sm"
                        overflow="auto"
                        maxH="400px"
                      >
                        {JSON.stringify(template.layout_structure, null, 2)}
                      </Code>
                    ) : (
                      <Alert status="info">
                        <AlertIcon />
                        No layout structure defined
                      </Alert>
                    )}
                  </VStack>
                </TabPanel>

                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <Text fontSize="sm" color="gray.600">
                      Default styles configuration
                    </Text>
                    
                    {template.styles ? (
                      <Code
                        display="block"
                        whiteSpace="pre"
                        p={4}
                        borderRadius="md"
                        fontSize="sm"
                        overflow="auto"
                        maxH="400px"
                      >
                        {JSON.stringify(template.styles, null, 2)}
                      </Code>
                    ) : (
                      <Alert status="info">
                        <AlertIcon />
                        No styles defined
                      </Alert>
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default ViewTemplate;